import { Card, Skeleton } from '@startupquickstart/frontend';
import { ArrowDown, ArrowUp, Trophy } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import { ARTIFACT_EFFECT_META } from '@/components/artifacts/demo/artifact-effect-meta';
import { RankPill } from '@/components/leaderboard/RankPill';
import {
  useEventLeaderboard,
  type LeaderboardEntry,
} from '@/components/events/live/useEventLeaderboard';
import { useEventActiveEffects, type ActiveEffect } from '@/hooks/useEventEffects';
import { DEFAULT_TEAM_ICON_COLOR, getTeamIcon } from '@/utils/teamIcons';

type RankDelta = 'up' | 'down' | null;

/** Category tint for a live effect badge icon. */
const EFFECT_ICON_COLOR: Record<ActiveEffect['teamArtifact']['artifact']['artifactCategory'], string> =
  {
    offensive: 'text-red-500',
    defensive: 'text-blue-500',
    wildcard: 'text-purple-500',
  };

/** Compact row of active-effect icons for a team on the Live scoreboard. */
function TeamEffectBadges({ effects }: { effects: ActiveEffect[] }) {
  if (effects.length === 0) return null;
  return (
    <span className="flex items-center gap-1">
      {effects.map((effect) => {
        const artifact = effect.teamArtifact.artifact;
        const Icon = ARTIFACT_EFFECT_META[artifact.artifactType]?.icon;
        if (!Icon) return null;
        return (
          <span
            key={effect.id}
            title={artifact.name}
            className="flex h-4 w-4 items-center justify-center"
          >
            <Icon className={`h-3.5 w-3.5 ${EFFECT_ICON_COLOR[artifact.artifactCategory]}`} />
          </span>
        );
      })}
    </span>
  );
}

function ScoreRow({
  entry,
  delta,
  effects,
}: {
  entry: LeaderboardEntry;
  delta: RankDelta;
  effects: ActiveEffect[];
}) {
  const TeamIcon = getTeamIcon(entry.teamIconName);
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <RankPill rank={entry.rank} />
      {/* Rank movement since the last update this session */}
      <span className="w-4 shrink-0">
        {delta === 'up' && <ArrowUp className="h-3.5 w-3.5 text-secondary" />}
        {delta === 'down' && <ArrowDown className="h-3.5 w-3.5 text-destructive" />}
      </span>
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: entry.teamIconColor || DEFAULT_TEAM_ICON_COLOR }}
      >
        <TeamIcon className="h-4 w-4 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-sm font-medium text-foreground">{entry.teamName}</p>
          <TeamEffectBadges effects={effects} />
        </div>
        <p className="text-xs text-muted-foreground">
          {entry.challengesCompleted} challenge{entry.challengesCompleted === 1 ? '' : 's'}
          {entry.allianceName ? ` · ${entry.allianceName}` : ''}
        </p>
      </div>
      <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
        {entry.totalPoints.toLocaleString()}
      </span>
    </li>
  );
}

/**
 * Live scoreboard panel for the command center: ranked teams with points,
 * and up/down arrows showing rank movement between refreshes (tracked
 * per mount — arrows reflect changes observed while watching).
 *
 * @param props - Target event id.
 */
export function LiveScoreboardPanel({ eventId }: { eventId: string }) {
  const { data, isLoading } = useEventLeaderboard(eventId);
  const { data: effectsData } = useEventActiveEffects(eventId);
  const entries = useMemo(() => data?.data?.entries ?? [], [data]);

  // Active effects grouped by the team they're applied to.
  const effectsByTeam = useMemo(() => {
    const map = new Map<string, ActiveEffect[]>();
    for (const effect of effectsData?.data ?? []) {
      const teamId = effect.targetTeam?.id;
      if (!teamId) continue;
      const list = map.get(teamId) ?? [];
      list.push(effect);
      map.set(teamId, list);
    }
    return map;
  }, [effectsData]);

  // Previous ranks by teamId, updated after each render with fresh data.
  const previousRanksRef = useRef<Map<string, number>>(new Map());
  const deltas = new Map<string, RankDelta>();
  for (const entry of entries) {
    const previous = previousRanksRef.current.get(entry.teamId);
    deltas.set(
      entry.teamId,
      previous === undefined || previous === entry.rank
        ? null
        : entry.rank < previous
          ? 'up'
          : 'down',
    );
  }

  useEffect(() => {
    if (entries.length === 0) return;
    previousRanksRef.current = new Map(entries.map((entry) => [entry.teamId, entry.rank]));
  }, [entries]);

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
          Scoreboard
        </h3>
        <Trophy className="h-4 w-4 text-muted-foreground" />
      </div>
      {isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Scores appear here once teams start completing challenges.
        </p>
      ) : (
        <ul className="max-h-[28rem] divide-y overflow-y-auto">
          {entries.map((entry) => (
            <ScoreRow
              key={entry.teamId}
              entry={entry}
              delta={deltas.get(entry.teamId) ?? null}
              effects={effectsByTeam.get(entry.teamId) ?? []}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}
